import { appStore } from '@/store/app'
import { Cartesian3 } from 'cesium'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import useDroneLocation from './useDroneLocation'
import { useGeoidHeight } from '@/composables/useGeoidHeight'
import { useMapFunctions } from '../useMapFunctions'
import log from '@/utils/useLogger'
import { CesiumService } from '@/handlers/cesium.service'

export const useElevation = () => {
  const appInstance = appStore()
  const { getGeoidHeight } = useGeoidHeight()
  const { droneLocation } = useDroneLocation()
  const { getTerrainHeight } = useMapFunctions()
  const { droneConfigurations, dockAmsl, isDjiDock3, telemetry, droneAmsl, geoidHeight } =
    storeToRefs(appInstance)
  const viewer = computed(() => CesiumService.viewer.value)

  const maxHeight = computed(() => {
    return isDjiDock3.value
      ? Number(droneConfigurations.value?.maxAltitude || 0) - (dockAmsl.value || 0)
      : Number(droneConfigurations.value?.maxAltitude || 0)
  })
  const takeOffHeight = computed(() => {
    return Number(droneConfigurations.value?.takeoffHeight || 0)
  })
  const ART = computed(() => {
    return Number(telemetry.value?.aircraftState?.height || 0)
  })
  const AGL = computed(() => {
    const difference =
      droneAmsl.value != null && dockAmsl.value != null
        ? Number(dockAmsl.value - droneAmsl.value)
        : 0
    return Number(ART.value + takeOffHeight.value + difference)
  })
  const droneAGL = computed(() => {
    return Number(droneAmsl.value) + AGL.value
  })
  const AMSL = computed(() => {
    const orthometricHeight = Number(droneAmsl.value - geoidHeight.value)
    return AGL.value + (isNaN(orthometricHeight) ? 0 : orthometricHeight)
  })
  const droneGroundLevel = computed(() => {
    if (droneAmsl.value == null || dockAmsl.value == null) return takeOffHeight.value
    return droneAmsl.value + takeOffHeight.value + (dockAmsl.value - droneAmsl.value)
  })
  const getInitialPosition = async () => {
    try {
      const terrainHeight = await getTerrainHeight(
        droneLocation.value.longitude,
        droneLocation.value.latitude,
      )
      droneAmsl.value = terrainHeight
      return Cartesian3.fromDegrees(
        droneLocation.value.longitude,
        droneLocation.value.latitude,
        terrainHeight + takeOffHeight.value,
      )
    } catch (error) {
      log.error('Error getting initial position', error)
      return null
    }
  }
  const calculatedMaxHeight = computed(() => {
    const maxAlt = Number(droneConfigurations.value?.maxAltitude || 0)
    const dock = Number(dockAmsl.value || 0)
    const takeoff = Number(takeOffHeight.value || 0)

    if (isDjiDock3.value) {
      const AGL = maxAlt - dock
      const ART = AGL - takeoff
      return {
        AGL,
        AMSL: maxAlt,
        ART: ART < 0 ? 0 : ART,
      }
    } else {
      const AGL = maxAlt
      const AMSL = AGL + dock
      const ART = AGL - takeoff
      return {
        AGL,
        AMSL,
        ART: ART < 0 ? 0 : ART,
      }
    }
  })
  const hasTelemetryLocation = computed(() => {
    return (
      telemetry.value?.locationNavigation?.longitude &&
      telemetry.value?.locationNavigation?.latitude
    )
  })
  const setAmsl = async (opts: { signal?: AbortSignal } = {}) => {
    try {
      if (!viewer.value || !viewer.value.terrainProvider) return
      const terrainHeight = await getTerrainHeight(
        droneLocation.value.longitude,
        droneLocation.value.latitude,
        { signal: opts.signal },
      )
      droneAmsl.value = terrainHeight
      // set geoid height
      if (!hasTelemetryLocation.value) return
      geoidHeight.value = await getGeoidHeight(
        Number(telemetry.value?.locationNavigation?.latitude),
        Number(telemetry.value?.locationNavigation?.longitude),
      )
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      log.error('Error setting Amsl:', error)
    }
  }
  return {
    maxHeight,
    takeOffHeight,
    ART,
    AGL,
    AMSL,
    droneAGL,
    getInitialPosition,
    droneGroundLevel,
    calculatedMaxHeight,
    setAmsl,
    getGeoidHeight,
    hasTelemetryLocation,
  }
}
